<template lang="pug">
.send-message-preview-component
  .send-message-wrapper
    .message
      .sub-title(v-if="showSubTitle") {{ _facilityName }}からのメッセージ
      .title.pre-line(v-text="value.title")
      .contents
        template(v-for="content in _contents")
          .text-content(v-if="content.type == 'text'")
            span {{ content.value }}
          .link-content.text-center(v-else)
            a(:href="content.uri" target="_blank") {{ content.value }}
      .expiration-date.pre-line(v-if="value.expirationDate" v-text="value.expirationDate")
      .expiration-text.pre-line(v-if="value.expirationText" v-text="value.expirationText")
      .sepalator
      .send-message-buttons.d-flex.flex-row.flex-wrap.align-items-stretch.mb-2
        .read.d-flex.align-items-stretch.justify-content-center(v-if="value.addReadButton")
          span 見ました
        .reply.d-flex.align-items-stretch.justify-content-center(v-if="value.addReplyButton")
          span 返信
      .option-text(v-if="value.addReplyButton && replyLimit") 返信可能期限は{{ replyLimit }}日以内です。
      .option-text(v-if="value.addOptionText") 「見ました」が押されなかった場合、施設から連絡をさせて頂く場合があります。
  .send-datetime {{ _convertDateTime }}
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { SendMessageFunction } from "@/commons/models/Message";

/**
 * メッセージ送信プレビュー
 */
@Component
export default class SendMessagePreview extends Vue {
  /** 表示対象のコンテンツ */
  @Prop({ required: true })
  value!: SendMessageFunction;

  @Prop()
  datetime!: number;

  @Prop({ default: true })
  showSubTitle!: boolean;

  @Prop()
  replyLimit!: number;

  /**
   * vue lifecycle created
   */
  public created(): void {
    return;
  }

  get _facilityName(): string {
    if (!userStore.facility) {
      return "施設名未登録";
    }

    return userStore.facility.name ? userStore.facility.name : "施設名未登録";
  }

  get _convertDateTime(): string {
    if (this.datetime) {
      return this.$dateUtil.convertNumberToString(this.datetime, "yyyy/mm/dd hh:MM");
    } else {
      return "";
    }
  }

  get _contents(): Array<{ type: string; value: string; uri?: string }> {
    // template側でlinkボタンとテキストを判別するためにtypeを持たせる
    let contents: Array<{ type: string; value: string; uri?: string }> = [];

    const splitedMessage: Array<string> = this.value.content.split("\n");
    let buttonCount: number = 1;

    for (const message of splitedMessage) {
      let content: { type: string; value: string; uri?: string };

      // リンクボタンメッセージ(ボタン名指定)
      // 先頭に半角全角スペースがあるだけの場合は無視して扱い、それ以外はテキスト文字列として扱う
      if (message.match(/^\[(.*)\]\((.*)\)$/)) {
        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const links: Array<{ name: string; url: string }> = [];
        let match: any;

        while ((match = linkRegex.exec(message)) !== null) {
          const name = match[1] ? match[1] : null;
          const url = match[2] ? match[2] : null;
          links.push({ name, url });
        }

        if (links.length == 1 && links[0].name != null && links[0].url != null) {
          content = {
            type: "link",
            value: `${links[0].name}`,
            uri: links[0].url
          };
          contents.push(content);
        } else {
          content = {
            type: "text",
            value: message
          };
          contents.push(content);
        }
      }
      // リンクボタンメッセージ(ボタン名指定無し)
      // 先頭に半角全角スペースがあるだけの場合はURLとして扱い、それ以外はテキスト文字列として扱う
      else if (message.match(/^\s*(http|https):\/\/.+/)) {
        // 前後の半角全角スペースを取り除き、URLデコード&エンコードを考慮したURIを作成する
        const uri: string = message.replace(/^\s+|\s+$/g, "");
        const decodedUri: string = decodeURI(uri);
        const encodedUri: string = encodeURI(decodedUri);

        content = {
          type: "link",
          value: `リンク その${buttonCount}`,
          uri: encodedUri
        };
        buttonCount++;
        contents.push(content);
      }
      // テキストメッセージ
      else {
        const convertedMessage: string = message == "" ? "　" : message;
        content = {
          type: "text",
          value: convertedMessage
        };
        contents.push(content);
      }
    }

    return contents;
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/pc/_colors.scss";
@import "../../styles/pc/_mixin.scss";

.send-message-preview-component {
  // .flex-previewにborderを指定すると背景画像がズレるので、枠線用の段落でラップする
  .send-message-wrapper {
    .message {
      background-color: #ffffff;
      padding: 10px;
      width: 420px;
      border-radius: 20px;

      .title {
        padding: 0 8px 8px 8px;
        font-weight: bold;
        font-size: 20px;
      }
      .sub-title {
        padding-left: 8px;
        font-size: 12px;
      }
      .contents {
        margin: 0;
        padding: 0 8px 0 8px;
        font-size: 16px;
        .link-content {
          padding: 4px;
        }
      }
      .expiration-date {
        padding-left: 8px;
        color: $date-font-color;
        font-size: 12px;
      }
      .expiration-text {
        padding-left: 8px;
        color: $date-font-color;
        font-size: 12px;
      }
      .option-text {
        padding: 0 0 8px 8px;
        font-size: 12px;
        color: $errormessage-font-color;
      }
      .send-message-buttons {
        border-top: 2px;

        div {
          flex: 1;

          margin: 8px 12px 0 12px;
          // ボタン風の見た目にする
          border-radius: 0.25rem;
          background-color: $btn !important;
          color: $btn-font !important;
          font-size: 18px !important;
          height: 42px !important;
          padding: 2px 10px !important;

          span {
            align-self: center;
          }
        }
      }
    }
  }
  .send-datetime {
    font-size: 14px;
    color: $date-font-color;
    text-align: left;
    font-weight: bold;
  }
}
</style>
